import type {
  ContestedMotion,
  MotionIntent,
} from "@/app/council/_lib/alignment-data";

// Pure filtering, sorting, pagination, vote-math, and label/source
// mapping behind <ContestedTimeline>. Extracted from the component so the
// multi-key sort comparator, the preview/page arithmetic, and the
// source-provenance label mapping can be unit-tested in isolation
// (tests/council/contested-timeline.test.ts). No React here — the
// component imports these and renders the result.

export type SortKey = "date-desc" | "date-asc" | "body" | "margin-tight" | "intent";

export const SORT_LABEL: Record<SortKey, string> = {
  "date-desc": "newest first",
  "date-asc": "oldest first",
  body: "by committee",
  "margin-tight": "closest vote first",
  intent: "by what the motion does",
};

const INTENT_ORDER: Record<MotionIntent, number> = {
  undo: 0,
  amend: 1,
  advance: 2,
  procedural: 3,
  other: 4,
};

export const INTENT_LABEL: Record<MotionIntent, string> = {
  advance: "advances a policy",
  amend: "amends a policy",
  undo: "rescinds a policy",
  procedural: "procedural",
  other: "other",
};

export const INTENT_TINT: Record<MotionIntent, string> = {
  advance: "var(--moss)",
  amend: "var(--amber)",
  undo: "var(--brick)",
  procedural: "var(--ink-3)",
  other: "var(--ink-3)",
};

/** Absolute for/against gap — the "closeness" of a contested vote. */
export function voteMargin(m: ContestedMotion): number {
  const f = m.vote_summary.for_count;
  const a = m.vote_summary.against_count;
  return Math.abs(f - a);
}

/** All ballots cast on a motion (for + against + recused + abstain). */
export function voteTotal(m: ContestedMotion): number {
  return (
    m.vote_summary.for_count +
    m.vote_summary.against_count +
    m.vote_summary.recused_count +
    m.vote_summary.abstain_count
  );
}

/** Sorted, de-duplicated list of the committees/bodies present in the set,
 *  for the body-filter pill row. */
export function deriveBodies(motions: ContestedMotion[]): string[] {
  const set = new Set<string>();
  for (const m of motions) set.add(m.category_name);
  return Array.from(set).sort();
}

/**
 * Filter by body then sort by the chosen key. Every non-date sort uses
 * date-desc as the stable tiebreaker so equal-key rows still read newest
 * first. `date-desc` is the default fallback for any unknown key.
 */
export function sortContested(
  motions: ContestedMotion[],
  bodyFilter: string,
  sortKey: SortKey,
): ContestedMotion[] {
  const filtered =
    bodyFilter === "all"
      ? motions
      : motions.filter((m) => m.category_name === bodyFilter);
  const arr = [...filtered];
  switch (sortKey) {
    case "date-asc":
      arr.sort((a, b) => a.meeting_date.localeCompare(b.meeting_date));
      break;
    case "body":
      arr.sort(
        (a, b) =>
          a.category_name.localeCompare(b.category_name) ||
          b.meeting_date.localeCompare(a.meeting_date),
      );
      break;
    case "margin-tight":
      arr.sort(
        (a, b) =>
          voteMargin(a) - voteMargin(b) ||
          b.meeting_date.localeCompare(a.meeting_date),
      );
      break;
    case "intent":
      arr.sort(
        (a, b) =>
          INTENT_ORDER[a.intent] - INTENT_ORDER[b.intent] ||
          b.meeting_date.localeCompare(a.meeting_date),
      );
      break;
    case "date-desc":
    default:
      arr.sort((a, b) => b.meeting_date.localeCompare(a.meeting_date));
  }
  return arr;
}

export type Pagination = {
  isPreview: boolean;
  totalPages: number;
  safePage: number;
  pageStart: number;
  pageEnd: number;
};

/**
 * Window arithmetic for the timeline. In preview mode (a numeric
 * `preview`) there's a single page showing the first `preview` rows and
 * no paging controls; otherwise rows are paged by `pageSize`. `safePage`
 * clamps a stale page index when the filtered set shrinks.
 */
export function paginate(
  total: number,
  page: number,
  pageSize: number,
  preview: number | undefined,
): Pagination {
  const isPreview = typeof preview === "number";
  // Defensive against misuse of the exported helper: a non-positive
  // pageSize would make Math.ceil(total / size) Infinity/NaN, and page < 1
  // would yield a negative pageStart. Callers today always pass size >= 1
  // and page >= 1, so this is a guard, not a behaviour change.
  const size = pageSize > 0 ? pageSize : 1;
  const totalPages = isPreview ? 1 : Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageStart = isPreview ? 0 : (safePage - 1) * size;
  const pageEnd = isPreview
    ? Math.min(preview ?? 0, total)
    : Math.min(pageStart + size, total);
  return { isPreview, totalPages, safePage, pageStart, pageEnd };
}

export function trimCouncillorPrefix(name: string): string {
  return name.replace(/^Councillor\s+/, "");
}

/** Long-form label for a non-direct vote-attribution source — explains to
 *  the reader how an individual vote was inferred when the minutes didn't
 *  name it directly. */
export function sourceShort(s: string): string {
  switch (s) {
    case "extrapolated_mover":
      return "inferred (moved or seconded)";
    case "extrapolated_attendance":
      return "inferred (chair declared a unanimous vote)";
    case "manual":
      return "entered manually";
    case "direct_count":
      return "counted, not named";
    case "unknown":
      return "source unknown";
    default:
      return s;
  }
}
